import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { TrendingUp, Users, Award, BarChart3, Target, Download, Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import StudentPerformanceView from "./StudentPerformanceView";
import SharedGradingDashboard from "./shared/SharedGradingDashboard";
import { db, auth } from "../firebase/config";
import { collection, getDocs, query, where } from "firebase/firestore";
import { onAuthStateChanged, getIdTokenResult, IdTokenResult } from "firebase/auth";

// Interfaces
interface ClassItem {
  id: string;
  name: string;
  classUid: string;
}

interface Subject {
  subjectId: string;
  name: string;
  code: string;
}

interface ExamSession {
  id: string;
  name: string;
  status: "active" | "inactive";
}

interface Grade {
  studentId: string;
  subjectId: string;
  examSessionId: string;
  marks: number | null;
  normalizedExamScore?: number;
  grade: string;
  studentClassUid: string;
}

interface Student {
  id: string;
  currentClassUid: string;
  firstName?: string;
  lastName?: string;
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface CustomClaims {
  affiliations?: Affiliation[];
  defaultAffiliation?: Affiliation;
  schoolID?: string;
}

const PerformanceAnalytics = () => {
  const { toast } = useToast();
  const [selectedTerm, setSelectedTerm] = useState<string>("");
  const [selectedClass, setSelectedClass] = useState<string>("all");
  const [isExporting, setIsExporting] = useState(false);
  const [showStudentView, setShowStudentView] = useState(false);
  const [showGradingView, setShowGradingView] = useState(false);

  // Firebase Data State
  const [schoolID, setSchoolID] = useState<string>("");
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [sessions, setSessions] = useState<ExamSession[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [students, setStudents] = useState<Student[]>([]);

  // Auth & Initial Data Fetch
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const idTokenResult: IdTokenResult = await getIdTokenResult(user, true);
          const claims = idTokenResult.claims as CustomClaims;
          const affiliations = claims.affiliations || [];
          const defaultAff = claims.defaultAffiliation;
          
          let targetSchoolID = claims.schoolID;
          
          if (!targetSchoolID) {
             const staffAff = defaultAff?.role === "school_staff" ? defaultAff : affiliations.find(a => a.role === "school_staff");
             if (staffAff?.schoolID) targetSchoolID = staffAff.schoolID;
          }

          if (targetSchoolID) {
            setSchoolID(targetSchoolID);
          }
        } catch (error) {
          console.error("Error fetching claims:", error);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch Metadata (Classes, Subjects, Sessions)
  useEffect(() => {
    if (!schoolID) return;

    const fetchData = async () => {
      try {
        // Classes
        const classesSnap = await getDocs(collection(db, `schools/${schoolID}/classes`));
        const classesData = classesSnap.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(),
          classUid: doc.data().classUid || doc.id
        } as ClassItem));
        setClasses(classesData);

        // Subjects
        const subjectsSnap = await getDocs(collection(db, `schools/${schoolID}/subjects`));
        setSubjects(subjectsSnap.docs.map(doc => ({ subjectId: doc.id, ...doc.data() } as Subject)));

        // Sessions
        const sessionsSnap = await getDocs(collection(db, `schools/${schoolID}/examSessions`));
        const sessionsData = sessionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as ExamSession));
        setSessions(sessionsData);
        
        // Set default term if available
        const activeSession = sessionsData.find(s => s.status === 'active');
        if (activeSession) setSelectedTerm(activeSession.id);
        else if (sessionsData.length > 0) setSelectedTerm(sessionsData[0].id);

      } catch (error) {
        console.error("Error fetching metadata:", error);
        toast({ title: "Error", description: "Failed to load school data", variant: "destructive" });
      }
    };
    fetchData();
  }, [schoolID, toast]);

  // Fetch Grades & Students based on filters
  useEffect(() => {
    if (!schoolID || !selectedTerm) return;

    const fetchAnalyticsData = async () => {
      try {
        // 1. Fetch Grades
        let gradesQuery = query(
          collection(db, `schools/${schoolID}/grades`),
          where("examSessionId", "==", selectedTerm)
        );

        if (selectedClass !== "all") {
          const classItem = classes.find(c => c.classUid === selectedClass);
          if (classItem) {
            gradesQuery = query(gradesQuery, where("studentClassUid", "==", selectedClass));
          }
        }

        const gradesSnap = await getDocs(gradesQuery);
        const gradesData = gradesSnap.docs.map(doc => doc.data() as Grade);
        setGrades(gradesData);

        // 2. Fetch Students (for total count)
        let studentsQuery = collection(db, `schools/${schoolID}/students`);
        let q = query(studentsQuery);
        
        if (selectedClass !== "all") {
           q = query(studentsQuery, where("currentClassUid", "==", selectedClass));
        }
        
        const studentsSnap = await getDocs(q);
        setStudents(studentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Student)));

      } catch (error) {
        console.error("Error fetching analytics data:", error);
      }
    };

    fetchAnalyticsData();
  }, [schoolID, selectedTerm, selectedClass, classes]);

  // --- Calculations ---

  const metrics = useMemo(() => {
    if (!grades.length) return {
      totalStudents: students.length,
      passRate: 0,
      avgScore: 0,
      topPerformers: 0
    };

    const uniqueStudentIds = new Set(grades.map(g => g.studentId));
    const totalGradedStudents = uniqueStudentIds.size;
    
    // Calculate average per student based on Subject Totals
    const studentAverages = Array.from(uniqueStudentIds).map(sId => {
      const studentGrades = grades.filter(g => g.studentId === sId);
      if (!studentGrades.length) return 0;

      // Group by subject to handle multi-paper subjects
      const subjectTotals = new Map<string, number>();
      studentGrades.forEach(g => {
        const score = g.normalizedExamScore !== undefined ? g.normalizedExamScore : (g.marks || 0);
        const current = subjectTotals.get(g.subjectId) || 0;
        subjectTotals.set(g.subjectId, current + score);
      });

      const subjects = Array.from(subjectTotals.values());
      const sum = subjects.reduce((acc, curr) => acc + curr, 0);
      return subjects.length ? sum / subjects.length : 0;
    });

    const overallSum = studentAverages.reduce((a, b) => a + b, 0);
    const avgScore = totalGradedStudents ? (overallSum / totalGradedStudents) : 0;

    // Pass rate (assuming 50 is pass for now)
    const passedCount = studentAverages.filter(avg => avg >= 50).length;
    const passRate = totalGradedStudents ? (passedCount / totalGradedStudents) * 100 : 0;

    // Top performers (e.g. > 80%)
    const topPerformers = studentAverages.filter(avg => avg >= 80).length;

    return {
      totalStudents: students.length || totalGradedStudents,
      passRate,
      avgScore,
      topPerformers
    };
  }, [grades, students]);

  const classPerformanceData = useMemo(() => {
    // Group by class
    const classMap = new Map<string, { total: number, sum: number, count: number }>();
    
    // We need to aggregate by student first to get their subject totals
    const studentSubjectTotals = new Map<string, Map<string, number>>(); // studentId -> subjectId -> totalScore
    const studentClassMap = new Map<string, string>(); // studentId -> classUid

    grades.forEach(g => {
      if (!studentSubjectTotals.has(g.studentId)) {
        studentSubjectTotals.set(g.studentId, new Map());
        studentClassMap.set(g.studentId, g.studentClassUid);
      }
      const subMap = studentSubjectTotals.get(g.studentId)!;
      const score = g.normalizedExamScore !== undefined ? g.normalizedExamScore : (g.marks || 0);
      subMap.set(g.subjectId, (subMap.get(g.subjectId) || 0) + score);
    });

    // Now calculate class averages based on student subject totals
    studentSubjectTotals.forEach((subMap, studentId) => {
      const classUid = studentClassMap.get(studentId);
      const cls = classes.find(c => c.classUid === classUid);
      const className = cls ? cls.name : "Unknown";

      if (!classMap.has(className)) {
        classMap.set(className, { total: 0, sum: 0, count: 0 });
      }
      const entry = classMap.get(className)!;
      
      // Add all subject totals for this student to the class sum
      subMap.forEach(score => {
        entry.sum += score;
        entry.count += 1; // Count each subject entry
      });
    });

    return Array.from(classMap.entries()).map(([name, data]) => ({
      class: name,
      average: data.count ? Math.round(data.sum / data.count) : 0
    }));
  }, [grades, classes]);

  const gradeDistribution = useMemo(() => {
    const dist: Record<string, number> = { A: 0, B: 0, C: 0, D: 0, E: 0, F: 0 };
    grades.forEach(g => {
      const baseGrade = g.grade ? g.grade.charAt(0).toUpperCase() : "N/A";
      if (dist[baseGrade] !== undefined) dist[baseGrade]++;
    });

    const colors: Record<string, string> = {
      "A": "#22c55e", "B": "#3b82f6", "C": "#f59e0b", "D": "#ef4444", "E": "#ef4444", "F": "#6b7280"
    };

    return Object.entries(dist).filter(([_, count]) => count > 0).map(([grade, count]) => ({
      grade,
      count,
      color: colors[grade] || "#888888"
    }));
  }, [grades]);

  const subjectPerformanceData = useMemo(() => {
    const subMap = new Map<string, { sum: number, count: number }>();
    
    // We need to aggregate scores by subject first (summing papers)
    // Key: subjectId_studentId -> totalScore
    const studentSubjectScores = new Map<string, number>();

    grades.forEach(g => {
      const key = `${g.subjectId}_${g.studentId}`;
      const score = g.normalizedExamScore !== undefined ? g.normalizedExamScore : (g.marks || 0);
      studentSubjectScores.set(key, (studentSubjectScores.get(key) || 0) + score);
    });

    // Now aggregate into subject averages
    studentSubjectScores.forEach((score, key) => {
      const [subjectId] = key.split('_');
      if (!subMap.has(subjectId)) {
        subMap.set(subjectId, { sum: 0, count: 0 });
      }
      const entry = subMap.get(subjectId)!;
      entry.sum += score;
      entry.count += 1;
    });

    return Array.from(subMap.entries()).map(([subId, data]) => {
      const subject = subjects.find(s => s.subjectId === subId);
      return {
        subject: subject ? subject.name : subId,
        average: data.count ? Math.round(data.sum / data.count) : 0,
        students: data.count
      };
    });
  }, [grades, subjects]);


  const handleExport = async () => {
    setIsExporting(true);
    setTimeout(() => {
      setIsExporting(false);
      toast({
        title: "Export Complete",
        description: "Performance analytics exported successfully"
      });
    }, 2000);
  };

  const handleShowStudents = () => {
    setShowStudentView(true);
  };

  if (showStudentView) {
    return <StudentPerformanceView onBack={() => setShowStudentView(false)} />;
  }

  if (showGradingView) {
    const selectedClassId = classes.find(c => c.classUid === selectedClass)?.id;
    return (
      <div className="flex flex-col h-full bg-white dark:bg-[#020817] rounded-lg border border-gray-200 dark:border-[#1E293B]">
        <div className="p-4 border-b border-gray-200 dark:border-[#1E293B] flex items-center justify-between">
           <Button variant="ghost" onClick={() => setShowGradingView(false)} className="gap-2">
             <TrendingUp className="h-4 w-4 rotate-180" />
             Back to Analytics
           </Button>
           <h2 className="text-lg font-semibold">Grade Management</h2>
        </div>
        <div className="flex-1 overflow-hidden flex flex-col">
            <SharedGradingDashboard 
                schoolID={schoolID} 
                userRole="admin" 
                defaultClassId={selectedClass !== "all" ? selectedClassId : undefined} 
            />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 sm:gap-4">
        <div>
          <h3 className="text-lg sm:text-xl font-semibold">Performance Analytics</h3>
          <p className="text-xs sm:text-sm text-muted-foreground">Comprehensive performance insights and trends</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
          <Select value={selectedTerm} onValueChange={setSelectedTerm}>
            <SelectTrigger className="w-full sm:w-[180px] bg-white dark:bg-[#020817] border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-gray-100">
              <SelectValue placeholder="Select Term" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-[#020817] border-gray-200 dark:border-[#1E293B]">
              {sessions.map(s => (
                <SelectItem key={s.id} value={s.id} className="focus:bg-gray-100 dark:focus:bg-[#1E293B]">{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={selectedClass} onValueChange={setSelectedClass}>
            <SelectTrigger className="w-full sm:w-[180px] bg-white dark:bg-[#020817] border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-gray-100">
              <SelectValue placeholder="Select Class" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-[#020817] border-gray-200 dark:border-[#1E293B]">
              <SelectItem value="all" className="focus:bg-gray-100 dark:focus:bg-[#1E293B]">All Classes</SelectItem>
              {classes.map(c => (
                <SelectItem key={c.id} value={c.classUid} className="focus:bg-gray-100 dark:focus:bg-[#1E293B]">{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={handleExport} disabled={isExporting}>
            <Download className="w-4 h-4 mr-2" />
            {isExporting ? "Exporting..." : "Export"}
          </Button>
          <Button 
            onClick={() => setShowGradingView(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
             <Award className="w-4 h-4 mr-2" />
             Grade Entry
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={handleShowStudents}>
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center justify-between">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <Users className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              </div>
              <Badge variant="secondary" className="text-xs">
                <Eye className="w-3 h-3 mr-1" />
                View
              </Badge>
            </div>
            <div className="mt-3 sm:mt-4">
              <p className="text-lg sm:text-2xl font-bold">{metrics.totalStudents}</p>
              <p className="text-xs sm:text-sm text-muted-foreground">Total Students</p>
            </div>
          </CardContent>
        </Card>

        {[
          { icon: Award, title: "Pass Rate", value: `${metrics.passRate.toFixed(1)}%`, color: "bg-green-500" },
          { icon: TrendingUp, title: "Avg Score", value: metrics.avgScore.toFixed(1), color: "bg-purple-500" },
          { icon: Target, title: "Top Performers", value: metrics.topPerformers, color: "bg-orange-500" }
        ].map((metric, index) => (
          <Card key={index}>
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center justify-between">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 ${metric.color} rounded-lg flex items-center justify-center`}>
                  <metric.icon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
              </div>
              <div className="mt-3 sm:mt-4">
                <p className="text-lg sm:text-2xl font-bold">{metric.value}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">{metric.title}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Class Performance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5" />
                Class Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  average: { label: "Average", color: "#3b82f6" },
                }}
                className="h-[300px] w-full"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={classPerformanceData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="class" fontSize={12} />
                    <YAxis fontSize={12} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="average" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>

          {/* Grade Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm sm:text-base">Grade Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  count: { label: "Students", color: "#3b82f6" },
                }}
                className="h-[300px] w-full"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <Pie
                      data={gradeDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ grade, count }) => `${grade}: ${count}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {gradeDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <ChartTooltip content={<ChartTooltipContent />} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
           {/* Subject Performance */}
           <Card>
            <CardHeader>
              <CardTitle className="text-sm sm:text-base">Subject Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  average: { label: "Average", color: "#10b981" },
                }}
                className="h-[300px] w-full"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={subjectPerformanceData} layout="vertical" margin={{ top: 20, right: 30, left: 40, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} hide />
                    <YAxis dataKey="subject" type="category" width={100} fontSize={12} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="average" fill="#10b981" radius={[0, 4, 4, 0]} barSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PerformanceAnalytics;